<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import { usePaymentsStore } from '@/stores/payments';
import MxAvatar from '@/components/MxAvatar.vue';
import MxButton from '@/components/MxButton.vue';
import MxCard from '@/components/MxCard.vue';
import MxChip from '@/components/MxChip.vue';
import MxSkeleton from '@/components/MxSkeleton.vue';

const userStore = useUserStore();
const paymentsStore = usePaymentsStore();
const { me } = storeToRefs(userStore);
const { items: payments, loading: payLoading, exhausted: payDone } = storeToRefs(
  paymentsStore,
);

const tg = (window as any).Telegram?.WebApp;
const photoUrl = computed(() => tg?.initDataUnsafe?.user?.photo_url ?? null);

const fullName = computed(() => {
  if (!me.value) return '';
  const u = me.value.user;
  return [u.first_name, u.last_name].filter(Boolean).join(' ');
});

const handle = computed(() =>
  me.value?.user.username ? `@${me.value.user.username}` : '',
);

function statusLabel(s: string): string {
  return (
    {
      success: 'Успешно',
      pending: 'В обработке',
      canceled: 'Отменён',
      refunded: 'Возврат',
    } as Record<string, string>
  )[s] ?? s;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

onMounted(() => paymentsStore.loadFirst());
</script>

<template>
  <section class="prof">
    <header class="hdr">
      <MxAvatar :src="photoUrl" :name="me?.user.first_name" :size="80" />
      <div>
        <h1>{{ fullName || '—' }}</h1>
        <p class="handle">{{ handle }}</p>
      </div>
    </header>

    <MxCard class="bal">
      <p class="bal__lbl">Ваш баланс</p>
      <p class="bal__val">
        <span v-if="me" class="num bal__num">{{ me.balance_tokens }}</span>
        <MxSkeleton v-else height="44px" />
        <span class="bal__unit" v-if="me">токенов</span>
      </p>
      <RouterLink to="/topup">
        <MxButton variant="primary">Пополнить</MxButton>
      </RouterLink>
    </MxCard>

    <h3 class="sect">История платежей</h3>
    <div v-if="payLoading && payments.length === 0" class="pays">
      <MxSkeleton v-for="n in 3" :key="n" height="60px" radius="var(--r-md)" />
    </div>
    <div v-else-if="payments.length === 0" class="empty">
      Платежей пока нет.
    </div>
    <ul v-else class="pays">
      <li v-for="p in payments" :key="p.id" class="pay">
        <div class="pay__main">
          <p class="pay__title">
            <span class="num">+{{ p.tokens }}</span> т.
            <span class="pay__sep">·</span>
            <span class="num">{{ p.amount_rub }}</span> ₽
          </p>
          <p class="pay__sub">{{ fmtDate(p.created_at) }}</p>
        </div>
        <MxChip :variant="p.status === 'success' ? 'accent' : 'default'">
          {{ statusLabel(p.status) }}
        </MxChip>
      </li>
    </ul>

    <p v-if="!payDone && payments.length > 0" class="more">
      <button @click="paymentsStore.loadMore" :disabled="payLoading">
        Показать ещё
      </button>
    </p>

    <h3 class="sect">Поддержка</h3>
    <a href="https://t.me/motorinschool_support" class="support">
      Написать в поддержку
    </a>
  </section>
</template>

<style scoped>
.prof { padding: var(--sp-3) 0 var(--sp-6); }
.hdr {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  margin-bottom: var(--sp-6);
}
.hdr h1 {
  font-family: var(--font-display);
  font-size: var(--fs-xl);
  font-weight: 700;
  letter-spacing: -0.02em;
}
.handle { color: var(--text-secondary); font-size: var(--fs-sm); }

.bal { display: flex; flex-direction: column; gap: var(--sp-3); }
.bal__lbl { color: var(--text-secondary); font-size: var(--fs-sm); }
.bal__val {
  font-size: var(--fs-hero);
  color: var(--accent);
  display: flex;
  align-items: baseline;
  gap: var(--sp-2);
}
.bal__num { font-weight: 500; }
.bal__unit { font-size: var(--fs-base); color: var(--text-secondary); }

.sect {
  font-size: var(--fs-sm);
  font-weight: 500;
  margin: var(--sp-6) 0 var(--sp-3);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.pay__sep { color: var(--text-tertiary); margin: 0 2px; }

.pays { display: flex; flex-direction: column; gap: var(--sp-2); }
.pay {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--sp-3);
  background: var(--bg-elevated);
  border-radius: var(--r-md);
}
.pay__title { font-weight: 600; }
.pay__sub { color: var(--text-tertiary); font-size: var(--fs-sm); }

.empty { color: var(--text-tertiary); padding: var(--sp-6) 0; }
.more { text-align: center; padding: var(--sp-4) 0; }
.more button { color: var(--accent); }

.support {
  display: block;
  padding: var(--sp-3);
  background: var(--bg-elevated);
  border-radius: var(--r-md);
  color: var(--text-primary);
}
</style>
